import { LinearGradient as RNLinearGradient } from 'expo-linear-gradient';
import type { HTMLAttributes } from 'react';
import { createElement } from 'react';
import { Image as RNImage } from 'react-native';
import { styled } from 'react-native-css';

// React Native props that are intentionally passed cross-platform, either directly or by
// components that forward a whole ViewProps/TextProps object. On native, they are handled by
// styled(@expo/html-elements); here they must not reach the DOM, or React warns about
// unrecognized attributes. Those with a DOM equivalent are mapped, the rest are dropped.
type HtmlProps = HTMLAttributes<HTMLElement> & {
	onPress?: () => void;
	hrefAttrs?: { download?: boolean | null; rel?: string | null; target?: string | null } | null;
	rel?: string;
	target?: string;
	// Mapped to their ARIA equivalents.
	accessibilityLabel?: string;
	accessibilityRole?: HTMLAttributes<HTMLElement>['role'];
	accessibilityState?: {
		busy?: boolean;
		checked?: boolean | 'mixed';
		disabled?: boolean;
		expanded?: boolean;
		selected?: boolean;
	};
	accessibilityValue?: { max?: number; min?: number; now?: number; text?: string };
	importantForAccessibility?: 'auto' | 'no' | 'no-hide-descendants' | 'yes';
	// Mapped to the DOM test hook react-native-web also uses.
	testID?: string;
	// Dropped: CSS covers these on web (line-clamp for truncation, pointer-events utilities).
	ellipsizeMode?: string;
	numberOfLines?: number;
	pointerEvents?: 'auto' | 'box-none' | 'box-only' | 'none';
	// Dropped: native-only, no DOM equivalent.
	accessible?: boolean;
	collapsable?: boolean;
	focusable?: boolean;
	maxFontSizeMultiplier?: number | null;
	onLayout?: unknown;
};

function createHtmlElement(tag: string, container = false) {
	return function HtmlElement({
		className,
		numberOfLines: _numberOfLines,
		ellipsizeMode: _ellipsizeMode,
		accessible: _accessible,
		collapsable: _collapsable,
		focusable: _focusable,
		maxFontSizeMultiplier: _maxFontSizeMultiplier,
		onLayout: _onLayout,
		onPress,
		hrefAttrs,
		rel,
		target,
		pointerEvents: _pointerEvents,
		accessibilityLabel,
		accessibilityRole,
		accessibilityState,
		accessibilityValue,
		importantForAccessibility,
		testID,
		...domProps
	}: HtmlProps) {
		return createElement(tag, {
			...domProps,
			rel: rel ?? hrefAttrs?.rel ?? undefined,
			target: target ?? hrefAttrs?.target ?? undefined,
			onClick:
				domProps.onClick ??
				(onPress
					? (event) => {
							event.preventDefault();
							onPress();
						}
					: undefined),
			className: container ? (className ? `rnw-view ${className}` : 'rnw-view') : className,
			'aria-busy': domProps['aria-busy'] ?? accessibilityState?.busy,
			'aria-checked': domProps['aria-checked'] ?? accessibilityState?.checked,
			'aria-disabled': domProps['aria-disabled'] ?? accessibilityState?.disabled,
			'aria-expanded': domProps['aria-expanded'] ?? accessibilityState?.expanded,
			'aria-selected': domProps['aria-selected'] ?? accessibilityState?.selected,
			// Only 'no-hide-descendants' hides the subtree; 'no' has no DOM equivalent, as aria-hidden
			// would also hide the descendants it is meant to keep exposed.
			'aria-hidden': domProps['aria-hidden'] ?? (importantForAccessibility === 'no-hide-descendants' || undefined),
			'aria-label': domProps['aria-label'] ?? accessibilityLabel,
			'aria-valuemax': domProps['aria-valuemax'] ?? accessibilityValue?.max,
			'aria-valuemin': domProps['aria-valuemin'] ?? accessibilityValue?.min,
			'aria-valuenow': domProps['aria-valuenow'] ?? accessibilityValue?.now,
			'aria-valuetext': domProps['aria-valuetext'] ?? accessibilityValue?.text,
			role: domProps.role ?? accessibilityRole,
			'data-testid': testID,
		});
	};
}

const Html = {
	A: createHtmlElement('a'),
	Article: createHtmlElement('article', true),
	Blockquote: createHtmlElement('blockquote', true),
	Br: createHtmlElement('br'),
	Div: createHtmlElement('div', true),
	Footer: createHtmlElement('footer', true),
	Hr: createHtmlElement('hr'),
	Image: styled(RNImage),
	h1: createHtmlElement('h1'),
	h2: createHtmlElement('h2'),
	h3: createHtmlElement('h3'),
	h4: createHtmlElement('h4'),
	h5: createHtmlElement('h5'),
	h6: createHtmlElement('h6'),
	Header: createHtmlElement('header', true),
	Figure: createHtmlElement('figure', true),
	LinearGradient: styled(RNLinearGradient),
	Li: createHtmlElement('li'),
	Main: createHtmlElement('main', true),
	Nav: createHtmlElement('nav', true),
	P: createHtmlElement('p'),
	Section: createHtmlElement('section', true),
	Span: createHtmlElement('span'),
	Ul: createHtmlElement('ul'),
	Ol: createHtmlElement('ol'),
	Video: createHtmlElement('video'),
	Audio: createHtmlElement('audio'),
} as const;

export default Html;
